import { useMemo, useState } from "react";
import Icon from "../Icon";
import { BtnPrimary, BtnGhost } from "../Atoms";
import SelectorPaso from "./SelectorPaso";
import NotionSelector from "./NotionSelector";
import SelectorIncidencia from "./SelectorIncidencia";
import SelectorReunion from "./SelectorReunion";
import SelectorTareaUnico from "../misTareas/SelectorTareaUnico";
import { AG_ORIGENES, agOrigen } from "../../constants";
import { getTipoColor, getTipoIcon, getTipoProd } from "../../utils";
import Campo from "../forms/Campo";

// Elegir QUE se va a cronometrar. Dos pasos, y los dos a la vista a la vez para
// poder rectificar: primero el TIPO del cuaderno, y el origen de ese tipo decide
// que buscador se ofrece.
//
// Enlazar nunca es obligatorio. Siempre hay una valvula de escape ("solo
// describirla") porque el caso real existe: una interrupcion que entra por
// telefono no tiene ticket, y obligar a inventarse uno es peor que apuntarla a
// mano.
// ── Las dos reglas del formulario, puras y aparte para poder probarlas ──────

// ¿Se ha elegido algo con lo que enlazar?
//
// Dejar el selector VACIO es una respuesta valida, no un formulario a medias:
// significa "esto no cuelga de nada, apuntalo con su descripcion". Antes habia
// que salir por el enlace de «Sin enlazar, solo describirla» para poder empezar,
// y eso convertia lo mas comun —que no siempre hay a que enlazar— en un desvio
// que habia que descubrir.
export function estaEnlazado({ origen, paso, pagina, ticket, reunion }) {
  if (origen === "paso")       return !!paso;
  if (origen === "notion")     return !!pagina;
  if (origen === "incidencia") return !!ticket;
  if (origen === "outlook")    return !!reunion;
  return false;
}

// Que falta para poder empezar, dicho con palabras y no con un booleano.
//
// Antes esto era una condicion suelta y el boton se quedaba apagado sin explicar
// nada: se pulsaba, no pasaba NADA, y parecia que el cronometro estaba roto. Un
// boton deshabilitado que no dice que le falta es un callejon sin salida.
//
// El enlace NO esta aqui: es opcional.
export function loQueFalta({ tipo, desc }) {
  if (!tipo) return "Elige de qué es";
  if (!String(desc || "").trim()) return "Escribe qué estás haciendo";
  return null;
}

export default function SelectorTarea({
  agendaTiposData = [], misPasos = [], notionItems = [], verIncidencias = false,
  // Lo que hay en Mis tareas (aplanado). Null = la persona no lo ve: se mantienen los dos
  // buscadores de siempre (paso de proyecto y Notion).
  poolMisTareas = null,
  guardando, onEmpezar, onCancelar, onAbrirPanel,
}) {
  const [tipo,    setTipo]    = useState(null);
  const [desc,    setDesc]    = useState("");
  const [tocada,  setTocada]  = useState(false);   // descripcion escrita a mano
  const [libre,   setLibre]   = useState(false);   // "solo describirla"
  const [paso,    setPaso]    = useState(null);    // { id, project_id }
  const [pagina,  setPagina]  = useState(null);    // external_id de Notion
  const [ticket,  setTicket]  = useState(null);
  const [reunion, setReunion] = useState(null);   // evento de Outlook
  const [viaNotion, setViaNotion] = useState(false); // enlazar con Notion en vez del paso
  const [tarea,   setTarea]   = useState(null);    // seleccion del selector unico (paso o Notion)
  const [abrirOpc, setAbrirOpc] = useState(false);   // tipo solo-descripcion: ¿enlazar con una tarea, opcional?

  const tipos = useMemo(
    () => agendaTiposData.filter(t => !t.archived)
      .slice().sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)),
    [agendaTiposData]);

  const origenTipo = tipo ? agOrigen(tipo) : "ninguno";

  // El trabajo de valor se apunta contra el proyecto, y los proyectos de verdad
  // estan en Notion: por eso un tipo de productividad ALTA con origen "paso"
  // puede enlazarse tambien con una tarea de Notion. El servidor admite uno de
  // los dos enlaces, nunca los dos. En los tipos de bajo valor no se ofrece:
  // una interrupcion no cuelga de una tarea de proyecto.
  const prodAlta = tipo ? getTipoProd(tipo.name, agendaTiposData) >= 70 : false;
  // Con Mis tareas, paso y Notion son UNA sola lista («Tarea»): no hay conmutador.
  // En un tipo de solo descripcion es opcional: se ofrece detras de «Enlazar con una tarea».
  const unificado = poolMisTareas !== null && !libre
    && (origenTipo === "paso" || origenTipo === "notion" || (origenTipo === "ninguno" && abrirOpc));
  const dobleEnlace = !unificado && prodAlta && origenTipo === "paso";

  const origen = libre ? "ninguno"
    : unificado ? (tarea?.kind === "notion" ? "notion" : "paso")
    : (dobleEnlace && viaNotion ? "notion" : origenTipo);
  const info = AG_ORIGENES[origen];

  // El origen "incidencia" no puede funcionar para quien no es de TIC: el panel
  // devuelve 403 en todas sus rutas. Se degrada a describirla a mano.
  const sinPermiso = origen === "incidencia" && !verIncidencias;

  const elegirTipo = t => {
    setTipo(t); setLibre(false); setViaNotion(false);
    setPaso(null); setPagina(null); setTicket(null); setReunion(null); setTarea(null); setAbrirOpc(false);
    if (!tocada) setDesc("");
  };

  // Cambiar de fuente suelta el enlace anterior: dejarlo puesto mandaria dos
  // enlaces y el servidor rechaza la tarea entera.
  const elegirFuente = notion => {
    setViaNotion(notion); setPaso(null); setPagina(null);
    if (!tocada) setDesc("");
  };

  // El titulo del enlace es la mejor descripcion por defecto, pero se puede
  // reescribir: si el usuario ya ha escrito algo, no se le pisa.
  const rellenar = txt => { if (!tocada && txt) setDesc(txt); };

  const enlazado = unificado ? !!tarea : estaEnlazado({ origen, paso, pagina, ticket, reunion });
  const falta    = loQueFalta({ tipo, desc });

  const listo = !falta && !guardando;

  const enviar = () => {
    if (!listo) return;
    onEmpezar({
      type: tipo.name,
      description: desc.trim(),
      // Sin enlace elegido, la entrada se guarda suelta. El servidor lo exige
      // explicitamente: con origen "paso" y sin task_id, validarOrigen la
      // rechaza entera.
      sin_enlazar: libre || sinPermiso || !enlazado,
      // Origen explicito: un tipo de solo descripcion no trae uno por defecto y el servidor ignoraria el enlace
      ...(unificado && tarea?.kind === "paso" ? { origen: "paso", task_id: tarea.id } : {}),
      // Un encargo que me han mandado: el servidor lo resuelve contra MI fila de Mis tareas
      ...(unificado && tarea?.kind === "suelta" ? { origen: "suelta", mis_tarea_id: tarea.id } : {}),
      ...(unificado && tarea?.kind === "notion"
        ? { origen: "notion", external_task_id: tarea.id, external_project_id: tarea.parent, notion_titulo: tarea.title } : {}),
      ...(!unificado && origen === "paso" && paso ? { task_id: paso.id } : {}),
      ...(!unificado && origen === "notion" && pagina
        ? { external_task_id: pagina.id, external_project_id: pagina.parent,
            notion_titulo: pagina.title } : {}),
      ...(origen === "outlook" && reunion
        ? { outlook_event_id: reunion.external_id } : {}),
      ...(origen === "incidencia" && ticket
        ? { incidencia_id: ticket.id, incidencia_ref: String(ticket.numero),
            incidencia_titulo: ticket.titulo,
            // Se manda QUIEN la pidio, no el equipo de Odoo: ese es el del
            // helpdesk que atiende y sale siempre el mismo. El servidor cruza el
            // contacto con la plantilla para saber a que departamento imputar.
            incidencia_contacto: ticket.contacto?.nombre || null,
            incidencia_email: ticket.email || null } : {}),
    }, unificado && tarea?.fuera ? { traer: { origen: tarea.kind, id: tarea.id, titulo: tarea.title } } : undefined);
  };

  const lb = { fontSize: 11, color: "var(--text3)", fontWeight: 700, display: "block",
               marginBottom: 6, textTransform: "uppercase", letterSpacing: ".06em" };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {/* Paso 1: el tipo */}
      <div>
        <Campo><label style={lb}>¿De qué es?</label>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {tipos.map(t => {
            const on = tipo?.id === t.id;
            const cl = getTipoColor(t.name, [], agendaTiposData);
            const ic = getTipoIcon(t.name, agendaTiposData);
            return (
              <button key={t.id} className="btn cw-tipo-chip" aria-pressed={on} onClick={() => elegirTipo(t)}
                style={{ display: "flex", alignItems: "center", gap: 5, padding: "5px 11px",
                  borderRadius: 20, cursor: "pointer", fontFamily: "var(--font)",
                  fontSize: 11.5, fontWeight: on ? 700 : 500,
                  border: "1px solid " + (on ? cl : cl + "44"),
                  background: on ? cl + "22" : "var(--surface)",
                  color: on ? cl : "var(--text2)", transition: "all .13s" }}>
                {ic ? <Icon name={ic} size={11} color={on ? cl : "var(--text3)"} />
                    : <span style={{ width: 6, height: 6, borderRadius: "50%", background: cl }} />}
                {t.name}
              </button>
            );
          })}
          {!tipos.length && (
            <span style={{ fontSize: 11.5, color: "var(--text3)", fontStyle: "italic" }}>
              No tienes tipos en el cuaderno todavía
            </span>
          )}
        </div></Campo>
      </div>

      {/* Paso 2: la fuente que dice el tipo */}
      {tipo && (
        <div>
          <div style={lb}>{unificado ? (origenTipo === "ninguno" ? "Tarea (opcional)" : "Tarea") : dobleEnlace && !libre ? "¿Con qué lo enlazas?" : info.label}</div>

          {/* Dos fuentes para el mismo trabajo: el paso del proyecto nativo o la
              tarea de Notion. Se enseñan las dos a la vez porque quien apunta no
              tiene por que saber en cual de los dos sitios vive su proyecto. */}
          {dobleEnlace && !libre && (
            <div style={{ display: "flex", gap: 6, marginBottom: 7 }}>
              {[[false, "Paso de proyecto", "diagram-3"], [true, "Tarea de Notion", "file-earmark-text"]]
                .map(([v, txt, ic]) => {
                  const on = viaNotion === v;
                  return (
                    <button key={txt} className="btn" aria-pressed={on} onClick={() => elegirFuente(v)}
                      style={{ display: "flex", alignItems: "center", gap: 5, padding: "4px 10px",
                        borderRadius: 7, cursor: "pointer", fontFamily: "var(--font)",
                        fontSize: 11, fontWeight: on ? 700 : 500,
                        border: "1px solid " + (on ? "var(--accent)" : "var(--border-ctl)"),
                        background: on ? "var(--accent-bg)" : "var(--surface)",
                        color: on ? "var(--accent)" : "var(--text3)" }}>
                      <Icon name={ic} size={10} color={on ? "var(--accent)" : "var(--text3)"} />{txt}
                    </button>
                  );
                })}
            </div>
          )}

          {sinPermiso ? (
            <div style={avisoS}>
              <Icon name="headset" size={11} color="var(--text3)"
                style={{ marginTop: 1, flexShrink: 0 }} />
              <span>El panel de incidencias es del equipo TIC. Describe la interrupción
                y el tiempo se apunta igual.</span>
            </div>
          ) : unificado ? (
            <SelectorTareaUnico pool={poolMisTareas} misPasos={misPasos} notionItems={notionItems} value={tarea}
              onChange={s => { setTarea(s); rellenar(s?.title); }} />
          ) : origen === "paso" ? (
            <SelectorPaso taskId={paso?.id} pasos={misPasos} bloquearNoDisponibles
              onChange={(t, p) => {
                setPaso(t ? { id: t, project_id: p } : null);
                rellenar(misPasos.find(x => String(x.id) === String(t))?.title);
              }} />
          ) : origen === "notion" ? (
            <NotionSelector linkedId={pagina?.id} notionItems={notionItems}
              onChange={(id, parent) => {
                setPagina(id ? { id, parent,
                  title: notionItems.find(i => i.external_id === id)?.title } : null);
                rellenar(notionItems.find(i => i.external_id === id)?.title);
              }} />
          ) : origen === "outlook" ? (
            <SelectorReunion eventoId={reunion?.external_id}
              onChange={e => { setReunion(e); rellenar(e?.title); }} />
          ) : origen === "incidencia" ? (
            <SelectorIncidencia ticket={ticket} onAbrirPanel={onAbrirPanel}
              onChange={x => { setTicket(x); rellenar(x?.titulo); }} />
          ) : (
            <div style={{ fontSize: 11.5, color: "var(--text3)" }}>
              {info.hint}
              {poolMisTareas !== null && origenTipo === "ninguno" && !libre && (
                <button type="button" className="btn" onClick={() => setAbrirOpc(true)}
                  style={{ display: "block", marginTop: 6, background: "transparent", border: "none", padding: 0,
                    cursor: "pointer", fontFamily: "var(--font)", fontSize: 11.5, color: "var(--accent)",
                    textDecoration: "underline", minHeight: 24 }}>
                  Enlazar con una tarea (opcional)
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* La descripcion es lo que el cuaderno necesita, y ademas es la clave por
          la que se agrupan los tramos del dia cuando no hay enlace. */}
      {tipo && (
        <div>
          <Campo><label style={lb}>¿Qué estás haciendo?</label>
          <input value={desc} autoFocus={origen === "ninguno"}
            onChange={e => { setDesc(e.target.value); setTocada(true); }}
            onKeyDown={e => { if (e.key === "Enter") enviar(); }}
            placeholder="Preparar el pedido del cliente"
            style={{ width: "100%", padding: "8px 10px", borderRadius: 8, boxSizing: "border-box",
              border: "1px solid var(--border-ctl)", background: "var(--surface)",
              color: "var(--text)", fontSize: 13, fontFamily: "var(--font)" }} /></Campo>
        </div>
      )}

      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <BtnPrimary onClick={enviar} disabled={!listo}>
          {guardando ? "Empezando…" : "Empezar"}
        </BtnPrimary>
        <BtnGhost onClick={onCancelar}>Cancelar</BtnGhost>
        {falta && !guardando && (
          <span style={{ fontSize: 11, color: "var(--text3)", display: "flex",
            alignItems: "center", gap: 5 }}>
            <Icon name="arrow-left" size={10} color="var(--text3)" />{falta}
          </span>
        )}
        {/* La pregunta que deja un selector vacio, contestada donde surge. Antes
            ahi habia un "Elige el paso del proyecto" que no era una pista sino
            una condicion: el boton estaba apagado hasta cumplirla. */}
        {!falta && !guardando && !enlazado && origen !== "ninguno" && !sinPermiso && (
          <span style={{ fontSize: 11, color: "var(--text3)" }}>
            Sin elegir nada, se apunta solo con la descripción
          </span>
        )}
        {/* Valvula de escape: enlazar es lo normal, pero no siempre se puede. */}
        {tipo && !libre && origen !== "ninguno" && !sinPermiso && (
          <button className="btn" onClick={() => { setLibre(true); setViaNotion(false); setPaso(null); setPagina(null); setTicket(null); setReunion(null); setTarea(null); }}
            style={{ marginLeft: "auto", background: "transparent", border: "none",
              cursor: "pointer", fontSize: 11, color: "var(--text3)",
              fontFamily: "var(--font)", textDecoration: "underline" }}>
            Sin enlazar, solo describirla
          </button>
        )}
        {libre && (
          <button className="btn" onClick={() => setLibre(false)}
            style={{ marginLeft: "auto", background: "transparent", border: "none",
              cursor: "pointer", fontSize: 11, color: "var(--accent)",
              fontFamily: "var(--font)", textDecoration: "underline" }}>
            Enlazarla con {AG_ORIGENES[origenTipo].label.toLowerCase()}
          </button>
        )}
      </div>
    </div>
  );
}

const avisoS = {
  padding: "7px 10px", borderRadius: 8, border: "1px dashed var(--border)",
  background: "var(--bg)", fontSize: 11, color: "var(--text3)",
  display: "flex", alignItems: "flex-start", gap: 6, lineHeight: 1.45,
};
